import { useState, useEffect } from "react";
import "../../assets/css/MyProfile/MyProfile.css";
import { useNavigate } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { BASE_URL } from "../../store/config";
import ErrorModal from "../common/ErrorModal";

/* ================= PASSWORD VALIDATION ================= */
const validatePassword = (password) => {
  if (password.length < 8)
    return "Password must be at least 8 characters long";
  if (!/[A-Z]/.test(password))
    return "Password must contain one uppercase letter";
  if (!/[a-z]/.test(password))
    return "Password must contain one lowercase letter";
  if (!/[0-9]/.test(password))
    return "Password must contain one number";
  if (!/[!@#$%^&*]/.test(password))
    return "Password must contain one special character";
  return null;
};

const DEFAULT_PROFILE_IMAGE =
  "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTVhHYHwsBuokf6mFxAQ5oGylyQfPALYYpz4A&s";

function MyProfile() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    contact: "",
    alternateContact: "",
    dob: "",
    gender: "",
    city: "",
    state: "",
    country: "",
    password: "",
    confirmPassword: "",
    organizationName: "",
    designation: "",
    instituteName: "",
  });

  const [profileImage, setProfileImage] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const getMaxDOB = () => {
    const today = new Date();
    const tenYearsAgo = new Date();
    tenYearsAgo.setFullYear(today.getFullYear() - 10);
    return tenYearsAgo.toISOString().split("T")[0];
  };


  /* ================= PASSWORD MATCH ================= */
  const passwordMatch =
    form.password &&
    form.confirmPassword &&
    form.password === form.confirmPassword;

  const passwordMismatch =
    form.password &&
    form.confirmPassword &&
    form.password !== form.confirmPassword;



  const handleChange = (e) => {
    // 🔹 Only update for contact & alternateContact
    if (e.target.name === "contact" || e.target.name === "alternateContact") {
      setForm({
        ...form,
        [e.target.name]: e.target.value.replace(/\D/g, ""),
      });
    } else {
      // 🔹 Original behavior (unchanged)
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(URL.createObjectURL(file));
      setProfileImageFile(file);
    }
  };


 


  const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const isValidPhone = (phone) => /^[0-9]{10}$/.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.contact && !isValidPhone(form.contact)) {
      setPasswordError("Contact number must be exactly 10 digits");
      setShowErrorModal(true);
      return;
    }


    if (form.dob) {
      const dobDate = new Date(form.dob);
      const today = new Date();
      if (dobDate > today) {
        setPasswordError("Invalid date of birth");
        setShowErrorModal(true);
        return;
      }
    }


    if (form.alternateContact && !isValidPhone(form.alternateContact)) {
      setPasswordError("Alternate contact number must be exactly 10 digits");
      setShowErrorModal(true);
      return;
    }

    if (form.password) {
      const error = validatePassword(form.password);
      if (error) {
        setPasswordError(error);
        setShowErrorModal(true);
        return;
      }

      if (form.password !== form.confirmPassword) {
        setPasswordError("Passwords do not match");
        setShowErrorModal(true);
        return;
      }
    }

    setEmailError("");
    setPasswordError("");
    setShowErrorModal(false);

    const token = localStorage.getItem("access_token");

    try {
      await fetch(`${BASE_URL}api/user/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          first_name: form.firstName,
          last_name: form.lastName,
          contact_no: form.contact,
          alternate_contact: form.alternateContact,
          dob: form.dob || null,
          gender: form.gender,
          city: form.city,
          state: form.state,
          country: form.country,
          organization_name: form.organizationName,
          designation: form.designation,
          institute_name: form.instituteName,
        }),
      });

      if (form.password) {
        await fetch(`${BASE_URL}api/user/password`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            new_password: form.password,
          }),
        });
      }

      if (profileImageFile) {
        const imgData = new FormData();
        imgData.append("file", profileImageFile);

        await fetch(
          `${BASE_URL}api/user/profile-image`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: imgData,
          }
        );
      }

      alert("Profile updated successfully!");
      setForm({
        ...form,
        password: "",
        confirmPassword: "",
      });
    } catch (error) {
      alert("Something went wrong");
    }
  };

  const handleRemoveProfileImage = async () => {
    const token = localStorage.getItem("access_token");

    try {
      // Call backend to remove image (recommended)
      await fetch(`${BASE_URL}api/user/profile-image`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Reset UI
      setProfileImage(null);
      setProfileImageFile(null);

      alert("Profile photo removed successfully!");
    } catch (err) {
      alert("Failed to remove profile photo");
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("access_token");

      const res = await fetch(
        `${BASE_URL}api/user/profile`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = (await res.json()) || {};

      setForm({
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        email: data.email || "",
        contact: data.contact_no || "",
        alternateContact: data.alternate_contact || "",
        dob: data.dob || "",
        gender: data.gender || "",
        city: data.city || "",
        state: data.state || "",
        country: data.country || "",
        organizationName: data.organization_name || "",
        designation: data.designation || "",
        instituteName: data.institute_name || "",
        password: "",
        confirmPassword: "",
      });

      if (data.profile_image) {
        setProfileImage(
          `${BASE_URL}${data.profile_image}`
        );
      }
    };

    fetchProfile();
  }, []);

  return (
    <div className="mp-profile-page">
      <div className="profile-header">
        <div className="bg-image"></div>

        <div className="profile-pic-wrapper">
          <label htmlFor="profileImageInput">
            <img
              src={profileImage || DEFAULT_PROFILE_IMAGE}
              className="profile-pic"
              alt="Profile"
            />
          </label>
          <input
            id="profileImageInput"
            type="file"
            hidden
            accept="image/*"
            onChange={handleImageChange}
          />
        </div>
      </div>

      {profileImage && (
        <button
          type="button"
          className="remove-photo-btn"
          onClick={handleRemoveProfileImage}
        >
          Remove Photo
        </button>
      )}

      <form className="profile-form" onSubmit={handleSubmit}>
        <h3 className="section-title">Personal Details</h3>

        <div className="form-grid">
          <div className="floating-field">
            <input
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              placeholder=" "
            />
            <label>First Name</label>
          </div>
          <div className="floating-field">
            <input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              placeholder=" "
            />
            <label>Last Name</label>
          </div>
          <div className="floating-field">
            <input
              name="email"
              value={form.email}
              readOnly
              className="readonly"
              title="Email cannot be changed"
              placeholder=" "
            />
            <label>Email</label>
          </div>
          <div className="floating-field">
            <input
              name="contact"
              value={form.contact}
              onChange={handleChange}
              placeholder=" "
              maxLength={10}
            />
            <label>Contact</label>
          </div>
          <div className="floating-field">
            <input
              name="alternateContact"
              value={form.alternateContact}
              onChange={handleChange}
              placeholder=" "
              maxLength={10}
            />
            <label>Alternate Contact</label>
          </div>
          <div className="floating-field">
            <input
              type="date"
              name="dob"
              value={form.dob}
              onChange={handleChange}
              max={getMaxDOB()}   // ✅ THIS WAS MISSING
            />
            <label>Date of Birth</label>
          </div>


          <div className="floating-field">
            <select
              name="gender"
              value={form.gender}
              onChange={handleChange}
            >
              <option value="">Select Gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
            <label>Gender</label>
          </div>

          <div className="floating-field">
            <input
              name="city"
              value={form.city}
              onChange={handleChange}
              placeholder=" "
            />
            <label>City</label>
          </div>

          <div className="floating-field">
            <input
              name="state"
              value={form.state}
              onChange={handleChange}
              placeholder=" "
            />
            <label>State</label>
          </div>

          <div className="floating-field">
            <input
              name="country"
              value={form.country}
              onChange={handleChange}
              placeholder=" "
            />
            <label>Country</label>
          </div>
        </div>

        <h3 className="section-title">Security</h3>

        <div className="form-grid">
          <div className="floating-field profile-password-field">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              id="password"
              value={form.password}
              onChange={handleChange}
              placeholder=" "
              className={
                passwordMatch ? "valid" : passwordMismatch ? "invalid" : ""
              }
            />
            <label htmlFor="password">New Password</label>

            <span
              className="eye-icon"
              onClick={() => setShowPassword(!showPassword)}
            >
              <FontAwesomeIcon
                icon={showPassword ? faEyeSlash : faEye}
              />
            </span>
          </div>

          <div className="floating-field profile-password-field">
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              id="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              placeholder=" "
              className={
                passwordMatch ? "valid" : passwordMismatch ? "invalid" : ""
              }
            />
            <label htmlFor="confirmPassword">Confirm Password</label>

            <span
              className="eye-icon"
              onClick={() =>
                setShowConfirmPassword(!showConfirmPassword)
              }
            >
              <FontAwesomeIcon
                icon={showConfirmPassword ? faEyeSlash : faEye}
              />
            </span>

            {passwordMismatch && (
              <span className="field-error">
                Passwords do not match
              </span>
            )}
          </div>
        </div>

        <h3 className="section-title">Professional Details</h3>

        <div className="form-grid">
          <div className="floating-field">
            <input
              name="organizationName"
              value={form.organizationName}
              onChange={handleChange}
              placeholder=" "
            />
            <label>Organization Name</label>
          </div>

          <div className="floating-field">
            <input
              name="designation"
              value={form.designation}
              onChange={handleChange}
              placeholder=" "
            />
            <label>Designation</label>
          </div>

          <div className="floating-field">
            <input
              name="instituteName"
              value={form.instituteName}
              onChange={handleChange}
              placeholder=" "
            />
            <label>Institute Name</label>
          </div>
        </div>

        <div className="form-buttons">
          <button type="submit" className="save-btn">
            Save Profile
          </button>

          <button
            type="button"
            className="close-btn"
            onClick={() => navigate("/dashboard")}
          >
            Close
          </button>
        </div>
      </form>

      <ErrorModal
        show={showErrorModal}
        message={emailError || passwordError}
        onClose={() => setShowErrorModal(false)}
        data-testid="error-modal"
      />

    </div>
  );
}

export default MyProfile;
